import { FC, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Grid2 as Grid, IconButton, Modal } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { useEventBus } from 'context/EventBusContext/EventBusContext';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useAttioValuationContext } from 'dashboard409a/context/AttioValuationContext';
import { useCompanyMeasurementDateByAttioValuationId } from 'dashboard409a/hooks/useCompanyMeasurementDateByAttioValuationId';
import {
  ChangeLog,
  EmptyOperationsMessage,
  ModelStatus,
  ValuationDealInfo,
  ValuedEntity,
  ValuedInformation,
} from 'dashboard409a/pages/Operations/components';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { StyledDivRoot, StyledGrid } from 'dashboard409a/pages/Operations/Operations.styles';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import {
  getValuationStatusOptions,
  sendFinalInvoice,
  updateValuationDealStage,
  updateValuationStatus,
} from 'dashboard409a/services/attio-common';
import { getAttioValuationDetail } from 'dashboard409a/services/attio-operation';
import { AttioValuationFormattedResponse, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { useCreateFulfillment, useFulfillmentList } from 'dashboard409a/services/fulfillment';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { Logger } from 'utilities';
import { CloseButtonContainer, StyledModalBox } from './OperationsModal.styles';

interface OperationsModalProps {
  open: boolean;
  onClose: () => void;
  data: ModelWithValuationData | SimplifiedAttioValuation | string | undefined;
  isLoading?: boolean;
  isFromModels?: boolean; // true if opened from Current/Past Models, false if from Current/Past Jobs
}

const OperationsModal: FC<OperationsModalProps> = ({
  open,
  onClose,
  data,
  isLoading: externalIsLoading = false,
  isFromModels = false,
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { publish } = useEventBus();
  const { setCurrentAttioCompanyName, setCurrentAttioValuation } = useAttioValuationContext();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Extract valuationId from either ModelWithValuationData, SimplifiedAttioValuation, or string
  const valuationId = useMemo(() => {
    if (!data) {
      return undefined;
    }
    if (typeof data === 'string') {
      return data;
    }
    // Check if it's ModelWithValuationData (has attio_valuation_id) or SimplifiedAttioValuation (has record_id)
    return 'attio_valuation_id' in data ? data.attio_valuation_id : data.record_id;
  }, [data]);

  const {
    data: valuationDetail,
    isError,
    error,
    isLoading = false,
    refetch: refetchValuationDetail,
  } = useQuery<AttioValuationDetailResponse, Error>({
    queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
    queryFn: () => getAttioValuationDetail(valuationId as string),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!valuationId && open, // Only run query if valuationId exists and modal is open
  });

  // Fetch company measurement date by attio_valuation_id - this includes the full company data
  const {
    data: companyMeasurementDateByAttioId,
    error: companyMeasurementDateError,
    isLoading: isLoadingCompanyMeasurementDate,
  } = useCompanyMeasurementDateByAttioValuationId(valuationId);

  // Extract company from companyMeasurementDateByAttioId
  const currentCompany = companyMeasurementDateByAttioId?.company;

  // Fetch status options from Attio API
  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Transform AttioStatusOption to StatusOption
  const statusOptions = attioStatusOptions.map(option => ({
    id: option.id.option_id,
    label: option.title,
    isArchived: option.is_archived,
  }));

  // Use the App company ID for fulfillment operations (not the Attio valued entity ID)
  // The fulfillment API expects the app company ID from companyMeasurementDateByAttioId
  const appCompanyId = useMemo(() => {
    if (currentCompany?.id) {
      return currentCompany.id.toString();
    }
    return undefined;
  }, [currentCompany?.id]);

  // Fetch fulfillment data to check if model support exists
  const { data: fulfillmentData, refetch: refetchFulfillments } = useFulfillmentList(appCompanyId);

  // Mutation for creating fulfillment
  const createFulfillmentMutation = useCreateFulfillment(appCompanyId);

  useEffect(() => {
    if (valuationDetail) {
      setCurrentAttioValuation(valuationDetail as AttioValuationFormattedResponse);
      if (valuationDetail?.valuedEntity.companyName) {
        setCurrentAttioCompanyName(valuationDetail?.valuedEntity.companyName);
      }
    }
  }, [valuationDetail, setCurrentAttioCompanyName, setCurrentAttioValuation]);

  if (isError) {
    Logger.error('Error fetching Attio valuations:', error);
  }

  const handleStatusChange = async (statusId: string) => {
    if (!valuationId) {
      return;
    }

    // Don't update if it's the same status
    if (valuationDetail?.statusId === statusId) {
      return;
    }

    setIsUpdatingStatus(true);

    try {
      await updateValuationStatus(valuationId, statusId);

      // Find the selected status label
      const selectedStatus = statusOptions.find(option => option.id === statusId);
      const statusLabel = selectedStatus?.label || 'status';

      // Publish event to update the table row
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId,
        updates: {
          status: statusLabel,
        },
      });

      // Invalidate queries in parallel for better performance
      const invalidationPromises = [
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.CURRENT_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.PAST_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATIONS],
          exact: false,
        }),
      ];

      await Promise.all(invalidationPromises);

      enqueueSnackbar(`Status has been updated to ${statusLabel}`, { variant: 'success' });
    } catch (statusError) {
      Logger.error('Error updating valuation status:', statusError);
      enqueueSnackbar('Failed to update status. Please try again.', { variant: 'error' });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDealStageChange = async (dealStageId: string) => {
    if (!dealStageId || !valuationDetail?.deal?.dealId) {
      return;
    }

    try {
      const {dealId} = valuationDetail.deal;
      await updateValuationDealStage(dealId, dealStageId);

      // Publish event to update the table row immediately
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId,
        updates: {
          dealStage: dealStageId,
        },
      });

      // Invalidate all relevant caches
      await Promise.all([
        // Invalidate the valuation detail query
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
          exact: false,
        }),
        // Invalidate the valuations list (for table refresh)
        queryClient.invalidateQueries({
          queryKey: ['valuations-all'],
          exact: false,
        }),
        // Invalidate current/past valuations
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.CURRENT_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.PAST_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATIONS],
          exact: false,
        }),
      ]);

      // Manually refetch the valuation detail to update the modal
      await refetchValuationDetail();

      enqueueSnackbar('Deal stage has been updated', { variant: 'success' });
    } catch (error) {
      Logger.error('Error updating deal stage:', error);
      enqueueSnackbar('Failed to update deal stage. Please try again.', { variant: 'error' });
    }
  };

  const handleSendFinalInvoice = async () => {
    await sendFinalInvoice(valuationDetail?.deal?.dealId as string);

    enqueueSnackbar('Final invoice has been sent', { variant: 'success' });
  };

  // Handler to update company folder
  const handleUpdateCompanyFolder = async (folderName: string) => {
    if (!currentCompany?.id) {
      const errorMessage = 'Company ID not available. Please ensure the company is properly linked.';
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      Logger.error('Company ID not available', {
        currentCompany,
        companyMeasurementDateByAttioId,
      });
      return;
    }

    try {
      await ApiService.apiCompaniesPartialUpdate(currentCompany.id, {
        file_storage_reference: folderName,
        is_deleted: false,
      } as Parameters<typeof ApiService.apiCompaniesPartialUpdate>[1]);

      // Invalidate company measurement date query to refetch updated data
      await queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });

      enqueueSnackbar('Company folder updated successfully', { variant: 'success' });
    } catch (error) {
      Logger.error('Error updating company folder:', error);
      enqueueSnackbar('Failed to update company folder. Please try again.', { variant: 'error' });
    }
  };

  // Handler to update valuation folder
  const handleUpdateValuationFolder = async (folderName: string) => {
    // Wait for company measurement date to load if still loading
    if (isLoadingCompanyMeasurementDate) {
      enqueueSnackbar('Loading measurement date information, please wait...', { variant: 'info' });
      return;
    }

    // Try to get measurement date ID from multiple sources
    let measurementDateId: number | undefined;

    if (companyMeasurementDateByAttioId) {
      measurementDateId = companyMeasurementDateByAttioId.id;
    }

    if (!measurementDateId) {
      const errorMessage = companyMeasurementDateError
        ? 'Unable to find measurement date for this valuation. Please ensure the valuation is linked to a measurement date.'
        : 'Measurement date ID not available. Please try refreshing the page.';
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
      Logger.error('Measurement date ID not available', {
        companyMeasurementDateByAttioId,
        valuationId,
        companyMeasurementDateError,
      });
      return;
    }

    try {
      await ApiService.apiCompanyMeasurementDatesPartialUpdate(measurementDateId, {
        file_storage_reference: folderName,
      });

      // Invalidate company measurement date query to refetch updated data
      await queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });

      enqueueSnackbar('Valuation folder updated successfully', { variant: 'success' });
    } catch (error) {
      Logger.error('Error updating valuation folder:', error);
      enqueueSnackbar('Failed to update valuation folder. Please try again.', { variant: 'error' });
    }
  };

  // Handle model support creation
  const handleCreateModelSupport = async () => {
    if (!appCompanyId) {
      enqueueSnackbar('Unable to create model support: Company information not available', {
        variant: 'error',
      });
      return;
    }

    try {
      // Use currentCompanyMeasurementDateId from global state, or fall back to the one fetched by attio_valuation_id
      const measurementDateId = companyMeasurementDateByAttioId?.id;
      if (!measurementDateId) {
        if (companyMeasurementDateError) {
          enqueueSnackbar(
            `Cannot create model support for ${currentCompany?.name || 'this company'}. The measurement date for this valuation (${valuationId}) was not found. Please check if the valuation ID is correct.`,
            {
              variant: 'error',
              autoHideDuration: 10000,
            }
          );
        } else {
          enqueueSnackbar(
            `Cannot create model support for ${currentCompany?.name || 'this company'}. No measurement dates exist for this company. Please create a measurement date first in the company dashboard.`,
            {
              variant: 'warning',
              autoHideDuration: 8000,
            }
          );
        }
        return;
      }

      await createFulfillmentMutation.mutateAsync({
        company_measurement_date: measurementDateId,
        model_status: modelStatusMap[ModelStatusEnum.DRAFT], // Start with status 0 (draft)
        supporting_due_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow's date in YYYY-MM-DD format
        reviewers: [],
        preparers: [],
      });

      // Refetch fulfillment data to update the UI
      refetchFulfillments();
    } catch (fulfillmentError) {
      Logger.error('Failed to create model support:', fulfillmentError);
      // Error handling is already done in the mutation
    }
  };

  const combinedIsLoading = isLoading || externalIsLoading;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
      }}>
      <StyledModalBox onClick={e => e.stopPropagation()}>
        <CloseButtonContainer>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </CloseButtonContainer>
        {valuationId ? (
          <StyledDivRoot container spacing={1}>
            <Grid size={{ md: 9, sm: 12 }}>
              <Grid container spacing={1}>
                <Grid size={{ md: 4, sm: 12 }}>
                  <Grid container spacing={1}>
                    <StyledGrid size={{ sm: 12 }}>
                      <ValuationDealInfo
                        deal={valuationDetail?.deal}
                        isLoading={combinedIsLoading}
                        onDealStageChange={handleDealStageChange}
                        onSendFinalInvoice={handleSendFinalInvoice}
                      />
                    </StyledGrid>
                    <StyledGrid size={{ sm: 12 }}>
                      <ValuedEntity
                        valuedEntity={valuationDetail?.valuedEntity}
                        isLoading={combinedIsLoading}
                        companyFolder={currentCompany?.file_storage_reference || null}
                        onUpdateCompanyFolder={handleUpdateCompanyFolder}
                      />
                    </StyledGrid>
                  </Grid>
                </Grid>
                {isFromModels ? (
                  <>
                    {/* When opened from Models: ModelStatus in the right column, ValuedInformation at the bottom */}
                    <Grid size={{ md: 8, sm: 12 }}>
                      <Grid container spacing={1}>
                        <StyledGrid size={{ sm: 12 }}>
                          <ModelStatus
                            fulfillmentData={fulfillmentData}
                            isLoading={combinedIsLoading}
                            onCreateModelSupport={handleCreateModelSupport}
                            isCreatingModelSupport={createFulfillmentMutation.isPending}
                            companyId={appCompanyId}
                            valuationId={valuationId}
                            companyMeasurementDateId={companyMeasurementDateByAttioId?.id}
                          />
                        </StyledGrid>
                      </Grid>
                    </Grid>
                    <Grid size={{ sm: 12 }}>
                      <ValuedInformation
                        valuedInformation={valuationDetail?.valuedInformation}
                        isLoading={combinedIsLoading}
                        statusOptions={statusOptions}
                        currentStatusId={valuationDetail?.statusId}
                        onStatusChange={handleStatusChange}
                        isStatusLoading={combinedIsLoading || isLoadingStatusOptions || isUpdatingStatus}
                        companyFolder={currentCompany?.file_storage_reference || null}
                        valuationFolder={companyMeasurementDateByAttioId?.file_storage_reference || null}
                        onUpdateValuationFolder={handleUpdateValuationFolder}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* When opened from Jobs: ValuedInformation in the right column, ModelStatus temporarily hidden */}
                    <Grid size={{ md: 8, sm: 12 }}>
                      <Grid container spacing={1}>
                        <StyledGrid size={{ sm: 12 }}>
                          <ValuedInformation
                            valuedInformation={valuationDetail?.valuedInformation}
                            isLoading={combinedIsLoading}
                            statusOptions={statusOptions}
                            currentStatusId={valuationDetail?.statusId}
                            onStatusChange={handleStatusChange}
                            isStatusLoading={combinedIsLoading || isLoadingStatusOptions || isUpdatingStatus}
                            companyFolder={currentCompany?.file_storage_reference || null}
                            valuationFolder={companyMeasurementDateByAttioId?.file_storage_reference || null}
                            onUpdateValuationFolder={handleUpdateValuationFolder}
                          />
                        </StyledGrid>
                      </Grid>
                    </Grid>
                    {/* ModelStatus temporarily hidden when opened from Jobs/Opportunities */}
                  </>
                )}
              </Grid>
            </Grid>
            <StyledGrid size={{ md: 3, sm: 12 }}>
              <ChangeLog />
            </StyledGrid>
          </StyledDivRoot>
        ) : (
          <EmptyOperationsMessage />
        )}
      </StyledModalBox>
    </Modal>
  );
};

export default OperationsModal;

